import { CmsComponentProps } from '@/types/cms';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { getHomePageDataByUrl } from '@/api/content';
import { twMerge } from 'tailwind-merge';
import useClickActionT2 from '@/tracks/useClickActionT2';
import Container from '../Layout/container';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import BGCOUPONFULL from './images/bg-coupon-full.png';
import BGCOUPONLITTLE from './images/bg-coupon-little.png';
import ICONARROW from './images/icon-arrow.png';
import Img from '@/components/common/Img';
import { getCroppedImageUrl } from '@/components/common/CroppedImage';
import CouponCard from '@/components/vendors/coupon/CouponCard';
import { isValidArray } from '@/utils/isValidArray';
import { CroppedImage } from '@weee-fe/module';
import { relativeLinkIfQAEnv } from '@/components/component-pool/tools/cms-link';

const CouponLine: FC<CmsComponentProps> = props => {
  const { skeleton, properties, postUrl, componentPos, cmsComponentKey, data: staticData, isStatic, className } = props;

  const isStaticDataSource = Boolean(isStatic);
  const [load, setLoad] = useState(!isStaticDataSource);
  const [data, setData] = useState<any>(staticData || {});
  const [visibleCouponCards, setVisibleCouponCards] = useState([]);
  const { commonClick } = useClickActionT2();
  const eventKey = properties?.event_key || cmsComponentKey;

  const fetchData = useCallback(async () => {
    const res = await getHomePageDataByUrl(postUrl);
    if (res?.result) {
      setData(res?.object || {});
    }
    setLoad(false);
  }, [postUrl]);

  useEffect(() => {
    if (isStaticDataSource) {
      setData(staticData || {});
      setLoad(false);
      return;
    }

    if (!postUrl) {
      setLoad(false);
      return;
    }

    fetchData();
  }, [fetchData, isStaticDataSource, postUrl, staticData]);

  const handleGoToStore = () => {
    if (!data?.link) return;
    window.location.href = relativeLinkIfQAEnv(data?.link);
  };

  const handleArrowToStore = e => {
    e.stopPropagation();
    handleGoToStore();
    commonClick({
      mod_nm: eventKey,
      mod_pos: componentPos,
      co: {
        target_nm: 'explore_more',
        target_type: 'banner_line',
        click_type: 'view',
        url: data?.link
      }
    });
  };

  const handleChange = useCallback(
    (inView, seller_id) => {
      setVisibleCouponCards(pre => {
        return pre.map(item => {
          if (item.seller_id === seller_id) {
            return { ...item, in_view: inView };
          }
          return item;
        });
      });
    },
    [visibleCouponCards]
  );

  useEffect(() => {
    const cards = [];
    data?.coupons?.forEach(item => {
      cards.push({
        seller_id: item.seller_id,
        in_view: false
      });
    });
    setVisibleCouponCards(cards);
  }, [data?.coupons]);

  const firstSellerInView = useMemo(() => {
    return visibleCouponCards?.find(item => item.in_view === true)?.seller_id ?? undefined;
  }, [visibleCouponCards]);

  const storeLink = relativeLinkIfQAEnv(data?.link);
  const hasCoupons = isValidArray(data?.coupons);

  if (load) {
    return skeleton;
  }

  if (!load && !Object?.keys(data)?.length) return null;

  return (
    <Container
      cmsComponentKey={props.cmsComponentKey}
      className={twMerge('overflow-hidden', className)}
      aria-label={properties?.title}>
      <div
        className={twMerge(
          'relative overflow-hidden box-border',
          hasCoupons ? 'h-[368px] py-[18px] px-5' : 'rounded-500 mx-5 pl-3 pr-5 h-[64px] flex items-center'
        )}
        onClick={handleGoToStore}>
        <div
          className={twMerge('absolute inset-0 z-0 pointer-events-none', !hasCoupons && 'rounded-500 overflow-hidden')}>
          <Img
            src={hasCoupons ? BGCOUPONFULL : BGCOUPONLITTLE}
            className={twMerge('w-full h-full object-cover')}
            alt=""
            aria-hidden="true"
          />
        </div>
        <div
          className={twMerge(
            'relative z-[1] flex items-center justify-between pb-[13px]',
            !hasCoupons ? 'pb-0 w-full' : ''
          )}>
          <div className="flex items-center justify-between">
            <CroppedImage
              url={data?.icon_url}
              configs={{
                renderWidth: 44
              }}
              wrapperClassName="w-11 h-11 rounded-700 mr-2.5 overflow-hidden flex-none"
              className="w-11 h-11 rounded-700"
              alt=""
              aria-hidden="true"
              showLoadingPlaceholder={false}
              data-testid="mod-coupon-list-icon"
            />
            <div data-testid="mod-coupon-list-title" className="enki-body-base-medium text-[#802204] line-clamp-2">
              {data?.title}
            </div>
          </div>
          <div>
            <a
              href={storeLink}
              onClick={handleArrowToStore}
              className="inline-block" // 保持布局
              aria-label={`Go to coupon landing page`}
              data-testid="btn-more-link">
              <Img
                className="w-[26px] h-[26px] rounded-400 flex-shrink-0 ml-2"
                src={getCroppedImageUrl(ICONARROW, { renderWidth: 86, devicePixelRatio: 2 })}
                alt=""
                aria-hidden="true"
                data-testid="btn-more-link"
              />
            </a>
          </div>
        </div>
        {/* product */}
        {hasCoupons && (
          <Swiper
            slidesPerView="auto"
            freeMode
            spaceBetween={10}
            className="!-mx-5 !pr-5"
            role="list"
            aria-label={`${properties?.title} coupons`}>
            {data?.coupons?.map((item, index) => {
              const isFirst = index === 0;
              return (
                <SwiperSlide
                  role="listitem"
                  key={`${item.id}-${index}`}
                  className={twMerge('!w-[350px]', isFirst ? 'ml-5' : '')}>
                  <CouponCard
                    info={item}
                    isUseInCms
                    index={index}
                    modNm={eventKey}
                    modPos={componentPos}
                    animationPlay={firstSellerInView === item.seller_id}
                    rootMargin="-103px 0px 0px 0px"
                    handleChange={handleChange}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}
      </div>
    </Container>
  );
};

export default CouponLine;
